import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { API_ENDPOINTS } from '../config/api';
import axios from 'axios';

interface NotificationContextType {
    unreadCount: number;
    refreshUnreadCount: () => Promise<void>;
    isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
    children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { isAuthenticated, token, user } = useAuth();

    const refreshUnreadCount = useCallback(async (): Promise<void> => {
        if (!isAuthenticated || !token) {
            setUnreadCount(0);
            return;
        }

        try {
            setIsLoading(true);
            
            let totalUnreadCount = 0;
            let hasMore = true;
            let nextToken: string | null = null;
            
            while (hasMore) {
                // Build URL with pagination
                const url = new URL(API_ENDPOINTS.MESSAGES.ENRICHED_CONVERSATIONS);
                url.searchParams.append('limit', '50');
                if (nextToken) {
                    url.searchParams.append('last_evaluated_key', nextToken);
                }

                const response = await axios.get(url.toString(), {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                const conversations = response.data.data || [];
                const pagination = response.data.pagination || {};

                // Calculate total unread count from enriched conversations
                for (const conv of conversations) {
                    // Ignore unknown users, matching Messages.tsx behavior
                    if (conv.participant_name !== 'Unknown User' && conv.unread_count > 0) {
                        totalUnreadCount += conv.unread_count;
                    }
                }
                
                hasMore = pagination.has_more || false;
                nextToken = pagination.next_token || null;
            }

            setUnreadCount(totalUnreadCount);
        } catch (error) {
            console.error('Error fetching unread count:', error);
            setUnreadCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, token, user?.id]);

    // Refresh unread count when authentication status changes
    useEffect(() => {
        if (isAuthenticated && user?.id) {
            refreshUnreadCount();
        } else {
            setUnreadCount(0);
        }
    }, [isAuthenticated, user?.id, refreshUnreadCount]);

    // Set up periodic refresh of unread count (every 30 seconds)
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            refreshUnreadCount();
        }, 30000); // 30 seconds

        return () => clearInterval(interval);
    }, [isAuthenticated, refreshUnreadCount]);

    const value: NotificationContextType = {
        unreadCount,
        refreshUnreadCount,
        isLoading,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotifications = (): NotificationContextType => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
